import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Dumbbell, Sparkles, Activity, Droplets, Lock, GlassWater,
  UserCheck, Sofa, MapPin, ExternalLink, Star, Baby,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { SITE } from "@/data/content";
import { useAdminContent } from "@/lib/adminContent";

const ICONS = { Dumbbell, Sparkles, Activity, Droplets, Lock, GlassWater, UserCheck, Sofa, Baby };

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Bento = () => {
  const { t, dict, lang } = useI18n();
  const { contacts } = useAdminContent(lang);
  const features = dict.bento.features;
  const reviews = dict.bento.reviews;
  const [mapVisible, setMapVisible] = useState(false);
  const mapRef = useRef(null);
  const mapLink = contacts?.mapShareUrl || SITE.openMapUrl || SITE.mapShareUrl;
  const mapAddress = contacts?.address || SITE.address;

  useEffect(() => {
    if (!mapRef.current || typeof window === "undefined" || mapVisible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setMapVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "180px 0px" }
    );
    observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, [mapVisible]);

  return (
    <section id="about" className="section" data-testid="bento-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-8 sm:mb-10 flex flex-col items-start gap-3"
        >
          <div className="eyebrow">{t("bento.label")}</div>
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight max-w-3xl" data-testid="bento-title">
            <span className="text-[var(--text)]">{t("bento.titleA")}</span>
            <br className="sm:hidden" />
            {" "}<span className="accent-text italic font-light">{t("bento.titleB")}</span>
          </h2>
          <p className="text-[var(--text-dim)] max-w-xl text-sm sm:text-base mt-1">{t("bento.description")}</p>
        </motion.div>

        <div className="grid grid-cols-12 gap-3 sm:gap-4 lg:gap-5">
          {/* SanoPass / ESX big card */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15, margin: "-60px" }}
            className="col-span-12 lg:col-span-7 glass rounded-2xl sm:rounded-3xl p-5 sm:p-8 relative overflow-hidden card-hover shine-parent"
            data-testid="bento-sanopass"
          >
            <div className="shine-line" />
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <span className="tag">{t("bento.partners")}</span>
              <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                <a
                  href="https://esx.ro"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[var(--text-dim)] hover:text-[var(--text)] inline-flex items-center gap-1 transition-colors"
                >
                  esx.ro <ExternalLink size={12} />
                </a>
                <a
                  href="https://sanopass.ro"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[var(--text-dim)] hover:text-[var(--text)] inline-flex items-center gap-1 transition-colors"
                >
                  sanopass.ro <ExternalLink size={12} />
                </a>
              </div>
            </div>

            <div className="mt-8 sm:mt-12 lg:mt-10 lg:grid lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] lg:items-center lg:gap-10">
              <div className="flex items-center justify-center gap-4 sm:gap-10 flex-wrap lg:flex-col lg:items-stretch lg:justify-start lg:gap-4">
                <a
                  href="https://esx.ro"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center px-5 sm:px-7 py-4 sm:py-5 rounded-2xl glass-strong h-20 sm:h-24 lg:h-32 min-w-[10rem] sm:min-w-[13rem] lg:min-w-0 lg:w-full"
                  data-testid="partner-esx"
                >
                  <img
                    src="/partners/esx-logo.svg"
                    alt="ESX"
                    width="220"
                    height="80"
                    className="h-9 sm:h-12 lg:h-16 w-auto logo-adapt"
                    data-invert="dark"
                  />
                </a>
                <a
                  href="https://sanopass.ro"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-3 px-5 sm:px-7 py-4 sm:py-5 rounded-2xl glass-strong h-20 sm:h-24 lg:h-32 min-w-[10rem] sm:min-w-[13rem] lg:min-w-0 lg:w-full"
                  data-testid="partner-sanopass"
                >
                  <img
                    src="/partners/sanopass-logo.png"
                    alt="SanoPass"
                    width="96"
                    height="96"
                    className="h-10 sm:h-14 lg:h-20 w-auto"
                  />
                  <span className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-[var(--text)]">
                    Sano<span className="accent-text">Pass</span>
                  </span>
                </a>
              </div>

              <p className="hidden lg:block text-sm text-[var(--text-dim)] leading-relaxed max-w-sm">
                {t("bento.partnerNote")}
              </p>
            </div>

            <p className="mt-7 sm:mt-8 text-xs sm:text-sm text-[var(--text-dim)] max-w-md leading-relaxed lg:hidden">
              {t("bento.partnerNote")}
            </p>

            <div className="absolute -bottom-24 -right-20 hidden sm:block w-72 h-72 rounded-full bg-[var(--accent)]/15 blur-3xl pointer-events-none" />
          </motion.div>

          <div className="col-span-12 lg:col-span-5 grid grid-cols-1 gap-3 sm:gap-4 lg:gap-5">
            <motion.div
              variants={fadeUp}
              custom={1}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className="glass rounded-2xl sm:rounded-3xl p-4 sm:p-5 relative overflow-hidden card-hover"
              data-testid="bento-map"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin size={14} className="text-[var(--accent)]" />
                  <span className="font-medium">MAX Fitness</span>
                </div>
                <a href={mapLink} target="_blank" rel="noreferrer" className="text-xs text-[var(--text-dim)] hover:text-[var(--text)] transition">
                  {t("bento.openMap")} →
                </a>
              </div>
              <div
                ref={mapRef}
                className="rounded-xl sm:rounded-2xl overflow-hidden border border-[var(--border)] aspect-[16/9] relative"
              >
                {mapVisible ? (
                  <iframe
                    src={SITE.mapEmbed}
                    width="100%"
                    height="100%"
                    className="map-frame"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    title="MAX Fitness location"
                  />
                ) : (
                  <a
                    href={mapLink}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute inset-0 bg-[var(--surface)] flex items-center justify-center text-sm text-[var(--text-dim)] hover:text-[var(--text)] transition-colors"
                  >
                    {t("bento.openMap")} →
                  </a>
                )}
              </div>
              <p className="mt-3 text-[11px] sm:text-xs text-[var(--text-mute)] leading-relaxed">{mapAddress}</p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={2}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-6 relative overflow-hidden card-hover flex items-center justify-between gap-4"
              data-testid="bento-reviews"
            >
              <div>
                <div className="eyebrow text-[10px]">{reviews.label}</div>
                <div className="mt-1 text-[var(--text-dim)] text-sm">{reviews.count}</div>
                <a
                  href={SITE.reviewsUrl || SITE.mapShareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium hover:accent-text transition-colors"
                  data-testid="bento-reviews-cta"
                >
                  {reviews.cta} <ExternalLink size={12} />
                </a>
              </div>
              <div className="text-right">
                <div className="font-display text-4xl sm:text-6xl font-bold accent-text leading-none tabular-nums">
                  {reviews.rating}
                </div>
                <div className="mt-2 flex justify-end gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={11} className="fill-[var(--accent)] text-[var(--accent)]" />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {features.map((f, i) => {
            const Icon = ICONS[f.icon] || Sparkles;
            return (
              <motion.div
                key={f.value + i}
                variants={fadeUp}
                custom={i + 3}
                initial="hidden"
                whileInView="show"
              viewport={{ once: true, amount: 0.15, margin: "-40px" }}
                className="col-span-6 sm:col-span-4 lg:col-span-3 glass rounded-2xl p-4 sm:p-5 card-hover relative overflow-hidden group"
                data-testid={`bento-feature-${i}`}
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[var(--accent)]/10 border border-[var(--accent)]/20 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[var(--accent)]/20 transition-colors">
                  <Icon size={17} className="text-[var(--accent)]" />
                </div>
                <div className="font-display text-xl sm:text-2xl font-bold tracking-tight">{f.value}</div>
                <div className="text-xs sm:text-sm text-[var(--text-dim)] mt-0.5">{f.label}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Bento;
